import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ConfirmDialog.module.css';
import { Button } from './Button';
import { DialogShell } from './DialogShell';
import { useDialogDismiss } from './dialogDismiss';
import { DialogHelp } from '../../help/DialogHelp';
import type { HelpContextId } from '../../help/contexts';

interface Props {
  isOpen: boolean;
  wide?: boolean;
  title: string;
  description: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
  loading?: boolean;
  requireReason?: boolean;
  reasonPlaceholder?: string;
  /** Trimmed-length bounds for the reason (only with requireReason). */
  reasonMinLength?: number;
  reasonMaxLength?: number;
  /**
   * Shows a live "n/max" counter under the reason and keeps Confirm disabled until the trimmed
   * reason fits reasonMinLength (default 1) … reasonMaxLength. Off by default (#281).
   */
  showReasonCounter?: boolean;
  /** Inline error shown above the actions (e.g. a failed request). */
  errorMessage?: string | null;
  variant?: 'danger' | 'primary' | 'success';
  /** Optional low-emphasis action left of Cancel (e.g. "Change shipper price"). */
  secondaryAction?: { label: string; onClick: () => void };
  /** Help context for the "?" in the header (src/help/contexts.ts); none = no "?". */
  help?: HelpContextId;
}

export function ConfirmDialog({
  isOpen,
  wide = false,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  loading,
  requireReason,
  reasonPlaceholder = 'Please enter a reason...',
  reasonMinLength,
  reasonMaxLength,
  showReasonCounter = false,
  errorMessage,
  variant = 'primary',
  secondaryAction,
  help,
}: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  // A reason box makes this a form dialog: no backdrop dismiss, typed reason asks first.
  const dismiss = useDialogDismiss({
    kind: requireReason ? 'form' : 'confirm',
    dirty: reason.trim() !== '',
    busy: loading,
    onClose: onCancel,
  });

  // Reset state when opened/closed
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmedLength = reason.trim().length;
  const counted = !!requireReason && showReasonCounter;
  const reasonFits =
    trimmedLength >= Math.max(1, reasonMinLength ?? 1) &&
    (reasonMaxLength == null || trimmedLength <= reasonMaxLength);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (requireReason && !reason.trim()) {
      setError('A reason is required to submit.');
      return;
    }
    if (requireReason && reasonMinLength != null && reason.trim().length < reasonMinLength) {
      setError(`Reason must be at least ${reasonMinLength} characters.`);
      return;
    }
    if (requireReason && reasonMaxLength != null && reason.trim().length > reasonMaxLength) {
      setError(`Reason must be ${reasonMaxLength} characters or fewer.`);
      return;
    }
    onConfirm(reason.trim() || undefined);
  };

  return (
    <DialogShell
      dismiss={dismiss}
      label={title}
      scrimClassName={styles.backdrop}
      panelClassName={`${styles.modal} ${wide ? styles.wide : ''}`}
    >
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        {help && <DialogHelp context={help} title={title} />}
        <button
          className={styles.closeBtn}
          onClick={dismiss.requestClose}
          disabled={loading}
          aria-label={t('common.actions.close')}
        >
          &times;
        </button>
      </div>
      <form onSubmit={handleSubmit} className={styles.body}>
        <div className={styles.description}>{description}</div>

        {requireReason && (
          <div className={styles.reasonWrapper}>
            <textarea
              className={`${styles.textarea} ${error ? styles.textareaError : ''}`}
              placeholder={reasonPlaceholder}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value.trim()) setError('');
              }}
              disabled={loading}
              rows={3}
              aria-label={t('ui.confirmDialog.reasonLabel')}
              maxLength={reasonMaxLength}
            />
            {error && <span className={styles.errorText}>{error}</span>}
            {counted && (
              <span className={styles.counter} aria-live="polite">
                {reasonMaxLength != null ? `${trimmedLength}/${reasonMaxLength}` : trimmedLength}
              </span>
            )}
          </div>
        )}

        {errorMessage && (
          <span role="alert" className={styles.errorText}>
            {errorMessage}
          </span>
        )}

        <div className={styles.actions}>
          {secondaryAction && (
            <Button
              variant="ghost"
              className={styles.secondaryAction}
              onClick={secondaryAction.onClick}
              disabled={loading}
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button variant="secondary" onClick={dismiss.requestClose} disabled={loading}>
            {cancelText}
          </Button>
          <Button
            variant={variant}
            type="submit"
            busy={loading}
            disabled={counted && !reasonFits}
          >
            {loading ? 'Processing...' : confirmText}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
